<template>
  <div class="banner-page">
    <section class="banner-toolbar">
      <div class="banner-toolbar__copy">
        <div class="banner-toolbar__title">轮播图设置</div>
        <div class="banner-toolbar__desc">
          维护小程序首页顶部的轮播图，并为每张图设置点击后跳转的页面。停用的图片不会在小程序展示。
        </div>
      </div>
      <div class="banner-toolbar__actions">
        <span class="banner-count">{{ banners.length }} 张</span>
        <button class="ghost-button" type="button" :disabled="loading" @click="reload">
          {{ loading ? "刷新中" : "刷新" }}
        </button>
        <button class="primary-button" type="button" @click="startCreate">新增轮播图</button>
      </div>
    </section>

    <section class="banner-workbench">
      <aside class="banner-list" aria-label="轮播图列表">
        <div v-if="loading" class="list-state">正在加载轮播图…</div>
        <div v-else-if="!banners.length" class="list-state">
          还没有轮播图，点右上角「新增轮播图」开始配置。
        </div>
        <div v-else class="list-body">
          <div
            v-for="(item, index) in banners"
            :key="String(item.id)"
            class="banner-item"
            :class="{ 'banner-item--active': form.id === item.id }"
            @click="selectBanner(item)"
          >
            <img class="banner-item__image" :src="resolveFileUrl(item.imageUrl)" alt="" />
            <div class="banner-item__body">
              <div class="banner-item__title">{{ item.title || "未命名轮播图" }}</div>
              <div class="banner-item__route">{{ routeText(item) }}</div>
            </div>
            <div class="banner-item__side">
              <span class="status-dot" :class="item.status === 1 ? 'status-dot--on' : 'status-dot--off'"></span>
              <div class="order-buttons">
                <button
                  type="button"
                  class="order-button"
                  :disabled="index === 0 || moving"
                  title="上移"
                  @click.stop="move(item, 'UP')"
                >↑</button>
                <button
                  type="button"
                  class="order-button"
                  :disabled="index === banners.length - 1 || moving"
                  title="下移"
                  @click.stop="move(item, 'DOWN')"
                >↓</button>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <article class="banner-editor">
        <header class="editor-header">
          <div>
            <div class="editor-header__eyebrow">
              {{ form.id ? "正在编辑 #" + form.id : "新增" }}
            </div>
            <h2>{{ form.id ? (form.title || "未命名轮播图") : "新增轮播图" }}</h2>
          </div>
          <span v-if="form.id" class="editor-header__hint">
            更新于 {{ formatDateTime(activeBanner && activeBanner.updatedAt) }}
          </span>
        </header>

        <div class="editor-body">
          <section class="editor-section">
            <div class="section-heading">
              <div>
                <h3>轮播图片</h3>
                <p>建议使用 750 × 300 左右的横图，比例差别太大会被裁切。</p>
              </div>
            </div>
            <div class="image-field">
              <div class="image-preview" :class="{ 'image-preview--empty': !form.imageUrl }">
                <img v-if="form.imageUrl" :src="resolveFileUrl(form.imageUrl)" alt="" />
                <span v-else>尚未上传</span>
              </div>
              <div class="image-actions">
                <button class="ghost-button" type="button" :disabled="uploading" @click="pickFile">
                  {{ uploading ? "上传中…" : "上传图片" }}
                </button>
                <button
                  v-if="form.imageUrl"
                  class="link-button"
                  type="button"
                  @click="form.imageUrl = ''"
                >移除</button>
                <input
                  ref="fileInput"
                  class="file-input"
                  type="file"
                  accept="image/*"
                  @change="onFileChange"
                />
              </div>
            </div>
          </section>

          <section class="editor-section">
            <div class="section-heading">
              <div>
                <h3>文案</h3>
                <p>留空则只展示图片，不叠加文字。</p>
              </div>
            </div>
            <label class="field">
              <span class="field__label">标题</span>
              <input v-model.trim="form.title" type="text" maxlength="64" placeholder="例如：秋季驱虫提醒" />
            </label>
            <label class="field">
              <span class="field__label">副标题</span>
              <input v-model.trim="form.subtitle" type="text" maxlength="128" placeholder="例如：一键生成驱虫计划" />
            </label>
          </section>

          <section class="editor-section">
            <div class="section-heading">
              <div>
                <h3>点击跳转</h3>
                <p>选择点击这张图之后打开的小程序页面。</p>
              </div>
            </div>
            <div class="segment">
              <button
                v-for="option in linkTypeOptions"
                :key="option.value"
                type="button"
                class="segment__item"
                :class="{ 'segment__item--active': form.linkType === option.value }"
                @click="selectLinkType(option.value)"
              >{{ option.label }}</button>
            </div>
            <label v-if="form.linkType === 'PAGE'" class="field">
              <span class="field__label">目标页面地址</span>
              <input v-model.trim="form.linkUrl" type="text" placeholder="例如：/pages/lottery-entry/index" />
              <span class="field__hint">
                需以 /pages/ 或 /pkg- 开头。底部标签页（首页、日历、广场、我的）会自动用切换标签的方式打开。
              </span>
              <span class="route-presets">
                <button
                  v-for="route in routes"
                  :key="route.path"
                  type="button"
                  class="route-preset"
                  :title="route.path"
                  @click="form.linkUrl = route.path"
                >
                  {{ route.label }}
                </button>
              </span>
              <span class="field__hint">点上方页面名可快速填入完整地址。</span>
            </label>
            <p v-else class="field__hint field__hint--block">这张图点击后不做跳转，仅作展示。</p>
          </section>

          <section class="editor-section">
            <div class="section-heading">
              <div>
                <h3>展示状态</h3>
                <p>停用后不会在小程序首页出现，配置会保留。</p>
              </div>
            </div>
            <label class="switch-row">
              <input v-model="statusEnabled" type="checkbox" />
              <span>{{ statusEnabled ? "已启用，展示在小程序首页" : "已停用，暂不展示" }}</span>
            </label>
          </section>
        </div>

        <footer class="editor-footer">
          <div class="editor-footer__state">
            <span v-if="message" :class="{ 'text-error': messageError }">{{ message }}</span>
            <span v-else>保存后立即生效，小程序下次进入首页即可看到。</span>
          </div>
          <div class="editor-footer__actions">
            <button
              v-if="form.id"
              class="danger-button"
              type="button"
              :disabled="saving"
              @click="removeCurrent"
            >删除</button>
            <button
              v-if="form.id"
              class="ghost-button"
              type="button"
              :disabled="saving"
              @click="startCreate"
            >改为新增</button>
            <button class="primary-button" type="button" :disabled="saving || uploading" @click="submit">
              {{ saving ? "保存中…" : "保存" }}
            </button>
          </div>
        </footer>
      </article>
    </section>
  </div>
</template>

<script>
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"
import {
  deleteBanner,
  fetchBannerRoutes,
  fetchBanners,
  moveBanner,
  saveBanner,
  uploadBannerImage
} from "../api/banner"

const POSITION_CODE = "home.top"

function emptyForm() {
  return {
    id: null,
    title: "",
    subtitle: "",
    imageUrl: "",
    linkType: "NONE",
    linkUrl: "",
    status: 1
  }
}

export default {
  name: "BannerSettingsView",
  data() {
    return {
      loading: false,
      saving: false,
      uploading: false,
      moving: false,
      message: "",
      messageError: false,
      banners: [],
      routes: [],
      form: emptyForm(),
      linkTypeOptions: [
        { value: "NONE", label: "不跳转" },
        { value: "PAGE", label: "跳转小程序页面" }
      ]
    }
  },
  computed: {
    activeBanner() {
      if (!this.form.id) return null
      return this.banners.find(item => item.id === this.form.id) || null
    },
    statusEnabled: {
      get() {
        return this.form.status === 1
      },
      set(value) {
        this.form.status = value ? 1 : 0
      }
    }
  },
  created() {
    this.loadRoutes()
    this.reload()
  },
  methods: {
    async loadRoutes() {
      try {
        const res = await fetchBannerRoutes()
        this.routes = Array.isArray(res.data) ? res.data : []
      } catch (error) {
        this.routes = []
      }
    },
    async reload() {
      this.loading = true
      try {
        const res = await fetchBanners({ positionCode: POSITION_CODE })
        this.banners = Array.isArray(res.data) ? res.data : []
        if (this.form.id) {
          const current = this.banners.find(item => item.id === this.form.id)
          if (current) {
            this.selectBanner(current)
          } else {
            this.form = emptyForm()
          }
        }
      } catch (error) {
        this.banners = []
        this.flash((error && error.message) || "轮播图加载失败", true)
      } finally {
        this.loading = false
      }
    },
    startCreate() {
      this.form = emptyForm()
      this.message = ""
      this.messageError = false
    },
    selectBanner(item) {
      this.form = {
        id: item.id,
        title: item.title || "",
        subtitle: item.subtitle || "",
        imageUrl: item.imageUrl || "",
        linkType: item.linkType || "NONE",
        linkUrl: item.linkUrl || "",
        status: item.status === 0 ? 0 : 1
      }
      this.message = ""
      this.messageError = false
    },
    selectLinkType(value) {
      this.form.linkType = value
      if (value === "NONE") {
        this.form.linkUrl = ""
      }
    },
    pickFile() {
      const input = this.$refs.fileInput
      if (input) input.click()
    },
    async onFileChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      this.uploading = true
      try {
        const res = await uploadBannerImage(file)
        const url = res.data && res.data.url
        if (url) {
          this.form.imageUrl = url
          this.flash("图片已上传，记得点保存")
        }
      } catch (error) {
        this.flash((error && error.message) || "图片上传失败", true)
      } finally {
        this.uploading = false
        event.target.value = ""
      }
    },
    async submit() {
      if (!this.form.imageUrl) {
        this.flash("请先上传轮播图片", true)
        return
      }
      if (this.form.linkType === "PAGE" && !this.form.linkUrl) {
        this.flash("请选择点击后跳转的页面", true)
        return
      }
      this.saving = true
      try {
        const res = await saveBanner({
          id: this.form.id,
          positionCode: POSITION_CODE,
          title: this.form.title || null,
          subtitle: this.form.subtitle || null,
          imageUrl: this.form.imageUrl,
          linkType: this.form.linkType,
          linkUrl: this.form.linkType === "PAGE" ? this.form.linkUrl : null,
          status: this.form.status
        })
        this.flash(res.message || "已保存")
        const savedId = res.data && res.data.id
        await this.reload()
        if (savedId) {
          const saved = this.banners.find(item => item.id === savedId)
          if (saved) this.selectBanner(saved)
        }
      } catch (error) {
        this.flash((error && error.message) || "保存失败", true)
      } finally {
        this.saving = false
      }
    },
    async removeCurrent() {
      if (!this.form.id) return
      const name = this.form.title || "这张轮播图"
      if (!window.confirm(`确认删除「${name}」？删除后小程序首页不再展示。`)) {
        return
      }
      this.saving = true
      try {
        await deleteBanner(this.form.id)
        this.flash("已删除")
        this.form = emptyForm()
        await this.reload()
      } catch (error) {
        this.flash((error && error.message) || "删除失败", true)
      } finally {
        this.saving = false
      }
    },
    async move(item, direction) {
      if (this.moving) return
      this.moving = true
      try {
        const res = await moveBanner(item.id, direction)
        if (Array.isArray(res.data)) {
          this.banners = res.data
        } else {
          await this.reload()
        }
      } catch (error) {
        this.flash((error && error.message) || "调整顺序失败", true)
      } finally {
        this.moving = false
      }
    },
    flash(text, isError = false) {
      this.message = text
      this.messageError = isError
    },
    routeText(item) {
      if (!item || item.linkType !== "PAGE") return "点击不跳转"
      return item.linkLabel ? `跳转 · ${item.linkLabel}` : `跳转 · ${item.linkUrl || "未设置"}`
    },
    resolveFileUrl(value) {
      return resolveBackendAssetUrl(value)
    },
    formatDateTime(value) {
      if (!value) return "-"
      return String(value).replace("T", " ").slice(0, 16)
    }
  }
}
</script>

<style scoped>
.banner-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
  height: 100%;
  color: #17221d;
}

.banner-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 22px;
  background: #fff;
  border: 1px solid #e5ebe7;
  border-radius: 16px;
  box-shadow: 0 10px 26px rgba(23, 34, 29, 0.05);
  flex-wrap: wrap;
}

.banner-toolbar__title {
  font-size: 20px;
  font-weight: 700;
}

.banner-toolbar__desc {
  margin-top: 6px;
  max-width: 620px;
  color: #6c7a73;
  font-size: 13px;
  line-height: 1.7;
}

.banner-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.banner-count {
  padding: 5px 12px;
  border-radius: 999px;
  background: #f2f5f3;
  color: #4d5c54;
  font-size: 12px;
}

.ghost-button,
.primary-button,
.danger-button {
  height: 36px;
  padding: 0 16px;
  border-radius: 10px;
  font-size: 13px;
  cursor: pointer;
}

.ghost-button {
  border: 1px solid #dfe6e1;
  background: #fff;
  color: #33463b;
}

.primary-button {
  border: 0;
  background: #0fbd68;
  color: #fff;
}

.danger-button {
  border: 1px solid #f0cdc5;
  background: #fff;
  color: #b23f2c;
}

.ghost-button:disabled,
.primary-button:disabled,
.danger-button:disabled,
.order-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.link-button {
  border: 0;
  background: transparent;
  color: #86928c;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
}

.banner-workbench {
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr);
  gap: 18px;
  flex: 1;
  min-height: 0;
}

.banner-list,
.banner-editor {
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #fff;
  border: 1px solid #e5ebe7;
  border-radius: 16px;
  box-shadow: 0 10px 26px rgba(23, 34, 29, 0.05);
}

.list-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.list-state {
  padding: 44px 20px;
  color: #86928c;
  font-size: 13px;
  text-align: center;
  line-height: 1.8;
}

.banner-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
}

.banner-item:hover {
  background: #f7faf8;
}

.banner-item--active {
  background: #eefaf2;
}

.banner-item__image {
  width: 76px;
  height: 44px;
  flex: 0 0 76px;
  object-fit: cover;
  border-radius: 8px;
  background: #f2f5f3;
}

.banner-item__body {
  flex: 1;
  min-width: 0;
}

.banner-item__title {
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.banner-item__route {
  margin-top: 4px;
  color: #86928c;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.banner-item__side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-dot--on {
  background: #0fbd68;
}

.status-dot--off {
  background: #cdd8d2;
}

.order-buttons {
  display: flex;
  gap: 4px;
}

.order-button {
  width: 24px;
  height: 24px;
  border: 1px solid #dfe6e1;
  border-radius: 6px;
  background: #fff;
  color: #4d5c54;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
}

.editor-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  border-bottom: 1px solid #eef2ef;
  flex-wrap: wrap;
}

.editor-header__eyebrow {
  color: #86928c;
  font-size: 12px;
}

.editor-header h2 {
  margin: 6px 0 0;
  font-size: 19px;
}

.editor-header__hint {
  color: #86928c;
  font-size: 12px;
}

.editor-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.editor-section {
  padding: 16px 18px;
  border: 1px solid #eef2ef;
  border-radius: 14px;
}

.section-heading {
  margin-bottom: 14px;
}

.section-heading h3 {
  margin: 0;
  font-size: 15px;
}

.section-heading p {
  margin: 4px 0 0;
  color: #86928c;
  font-size: 12px;
  line-height: 1.7;
}

.image-field {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.image-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 260px;
  height: 104px;
  border: 1px solid #eef2ef;
  border-radius: 10px;
  background: #fafcfb;
  overflow: hidden;
  color: #a4aea8;
  font-size: 12px;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-preview--empty {
  border-style: dashed;
}

.image-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.file-input {
  display: none;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
}

.field__label {
  font-size: 13px;
  color: #33463b;
}

.field input[type="text"],
.field select {
  height: 38px;
  padding: 0 12px;
  border: 1px solid #dfe6e1;
  border-radius: 10px;
  background: #fff;
  color: #1f2d26;
  font-size: 13px;
  outline: none;
}

.field__hint {
  color: #97a29c;
  font-size: 12px;
  line-height: 1.7;
}

.field__hint--block {
  margin: 0;
  color: #6c7a73;
}

.route-presets {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.route-preset {
  padding: 5px 10px;
  border: 1px solid #dfe6e1;
  border-radius: 999px;
  background: #fff;
  color: #4d5c54;
  font-size: 12px;
  cursor: pointer;
}

.route-preset:hover {
  border-color: #0fbd68;
  color: #14794a;
}

.segment {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  background: #f2f5f3;
  border-radius: 12px;
}

.segment__item {
  padding: 8px 16px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #5d6b64;
  font-size: 13px;
  cursor: pointer;
}

.segment__item--active {
  background: #fff;
  color: #12301f;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(23, 34, 29, 0.08);
}

.switch-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #33463b;
}

.switch-row input {
  width: 16px;
  height: 16px;
}

.editor-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 22px;
  border-top: 1px solid #eef2ef;
  flex-wrap: wrap;
}

.editor-footer__state {
  color: #6c7a73;
  font-size: 13px;
}

.text-error {
  color: #b23f2c;
}

.editor-footer__actions {
  display: flex;
  gap: 10px;
}

@media (max-width: 1080px) {
  .banner-workbench {
    grid-template-columns: 1fr;
  }
}
</style>
